/**
 * taskflow-templates.ts
 *
 * Pre-configured Project & Workflow Boilerplate Templates for TaskFlow.
 * Includes Product Launch, Client Onboarding, and Bug Triage blueprints with relative date arithmetic.
 */

import {
  type TaskFlowItem,
  type TaskFlowItemType,
  createTaskFlowItem,
} from './taskflow-schema.js';

export interface TemplateNodeBlueprint {
  tempId: string;
  parentTempId?: string | null;
  type: TaskFlowItemType;
  title: string;
  description?: string;
  status?: string;
  priority?: 'low' | 'normal' | 'medium' | 'high' | 'urgent';
  relativeStartDays?: number;
  relativeDueDays?: number;
  tags?: string[];
}

export interface TaskFlowTemplate {
  id: string;
  name: string;
  description: string;
  category: 'engineering' | 'product' | 'operations' | 'crm';
  nodes: TemplateNodeBlueprint[];
}

export const TASKFLOW_TEMPLATES: TaskFlowTemplate[] = [
  {
    id: 'product-launch',
    name: '🚀 Product Launch Campaign',
    description: 'End-to-end launch plan covering feature freeze, security audit, marketing collateral, and production release.',
    category: 'product',
    nodes: [
      {
        tempId: 'root',
        type: 'project',
        title: 'Product Launch v1.0',
        status: 'in-progress',
        priority: 'high',
        relativeStartDays: 0,
        relativeDueDays: 30,
        tags: ['release', 'v1.0'],
      },
      {
        tempId: 't1',
        parentTempId: 'root',
        type: 'task',
        title: 'Feature Freeze & Code Lockdown',
        status: 'backlog',
        priority: 'high',
        relativeStartDays: 0,
        relativeDueDays: 7,
        tags: ['engineering'],
      },
      {
        tempId: 't2',
        parentTempId: 'root',
        type: 'task',
        title: 'Security & PQC Audit Review',
        status: 'backlog',
        priority: 'urgent',
        relativeStartDays: 7,
        relativeDueDays: 14,
        tags: ['security', 'audit'],
      },
      {
        tempId: 't3',
        parentTempId: 'root',
        type: 'task',
        title: 'Marketing Collateral & Landing Page',
        status: 'backlog',
        priority: 'normal',
        relativeStartDays: 14,
        relativeDueDays: 25,
        tags: ['marketing'],
      },
      {
        tempId: 't4',
        parentTempId: 'root',
        type: 'task',
        title: 'Launch Release & Production Deployment',
        status: 'backlog',
        priority: 'urgent',
        relativeStartDays: 25,
        relativeDueDays: 30,
        tags: ['deployment'],
      },
    ],
  },
  {
    id: 'client-onboarding',
    name: '🤝 Client Onboarding Protocol',
    description: 'Standard enterprise onboarding: initial discovery, environment provisioning, data migration, and training.',
    category: 'operations',
    nodes: [
      {
        tempId: 'root',
        type: 'project',
        title: 'Enterprise Client Onboarding',
        status: 'in-progress',
        priority: 'high',
        relativeStartDays: 0,
        relativeDueDays: 14,
        tags: ['client', 'onboarding'],
      },
      {
        tempId: 't1',
        parentTempId: 'root',
        type: 'task',
        title: 'Kickoff & Discovery Workshop',
        status: 'backlog',
        priority: 'high',
        relativeStartDays: 0,
        relativeDueDays: 3,
      },
      {
        tempId: 't2',
        parentTempId: 'root',
        type: 'task',
        title: 'Sovereign Node Provisioning',
        status: 'backlog',
        priority: 'medium',
        relativeStartDays: 3,
        relativeDueDays: 7,
      },
      {
        tempId: 't3',
        parentTempId: 'root',
        type: 'task',
        title: 'Team Training & Handover',
        status: 'backlog',
        priority: 'normal',
        relativeStartDays: 7,
        relativeDueDays: 14,
      },
    ],
  },
  {
    id: 'bug-triage',
    name: '🐛 Critical Bug Triage & Hotfix',
    description: 'Rapid triage workflow for high-severity issues: reproduction, patch development, regression testing, and deployment.',
    category: 'engineering',
    nodes: [
      {
        tempId: 'root',
        type: 'task',
        title: 'Critical Bug Triage',
        status: 'in-progress',
        priority: 'urgent',
        relativeStartDays: 0,
        relativeDueDays: 2,
        tags: ['bug', 'hotfix'],
      },
      {
        tempId: 'a1',
        parentTempId: 'root',
        type: 'action',
        title: 'Isolate & Reproduce Bug',
        status: 'backlog',
        priority: 'urgent',
        relativeStartDays: 0,
        relativeDueDays: 1,
      },
      {
        tempId: 'a2',
        parentTempId: 'root',
        type: 'action',
        title: 'Author Hotfix Patch',
        status: 'backlog',
        priority: 'urgent',
        relativeStartDays: 1,
        relativeDueDays: 2,
      },
    ],
  },
];

export class TaskFlowTemplateRegistry {
  static getAvailableTemplates(): TaskFlowTemplate[] {
    return TASKFLOW_TEMPLATES;
  }

  static instantiateTemplate(
    templateId: string,
    options: { workspaceId: string; baseDate?: string; actorId?: string }
  ): TaskFlowItem[] {
    const template = TASKFLOW_TEMPLATES.find((t) => t.id === templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }

    const baseMs = options.baseDate ? new Date(options.baseDate).getTime() : Date.now();
    const tempIdToRealId = new Map<string, string>();
    const instantiated: TaskFlowItem[] = [];

    // 1. Assign real IDs
    for (const node of template.nodes) {
      const realId = `${node.type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      tempIdToRealId.set(node.tempId, realId);
    }

    // 2. Build full items with resolved parent and relative dates
    for (const node of template.nodes) {
      const realId = tempIdToRealId.get(node.tempId)!;
      const parentId = node.parentTempId ? tempIdToRealId.get(node.parentTempId) || null : null;

      let startDate: string | undefined;
      let dueDate: string | undefined;

      if (node.relativeStartDays !== undefined) {
        startDate = new Date(baseMs + node.relativeStartDays * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0];
      }

      if (node.relativeDueDays !== undefined) {
        dueDate = new Date(baseMs + node.relativeDueDays * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0];
      }

      const item = createTaskFlowItem(node.type, {
        id: realId,
        title: node.title,
        description: node.description,
        status: node.status || 'backlog',
        priority: node.priority || 'normal',
        startDate,
        dueDate,
        parent: parentId,
        tags: node.tags || [],
      });

      instantiated.push(item);
    }

    return instantiated;
  }
}
